/**
 * Calendar - Unified calendar with activities, events, bookings, shared bookings
 * Doc: docs/overall-plan/02-MANGO-PERSONAL/calendar/UNIFIED-CALENDAR-TIME-TRACKING-SPEC.md
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { bookingsApi, personalActivitiesApi, personalCalendarApi, connectionsApi, hangoutInvitesApi, groupsApi, MANGO_CHAT_URL } from '../services/api';
import { showToast } from 'shared-ui';

interface PersonalActivity {
  id: string;
  name: string;
  frequency: string;
  durationMinutes: number;
  color?: string;
  isActive: boolean;
}

interface CalendarItem {
  id: string;
  type: 'personal_event' | 'activity_session' | 'shared_booking' | 'business_event' | 'staff_job_session' | 'booking' | 'hangout_invite' | 'chat_event_invite' | 'chat_dinner_plan' | 'chat_find_a_time';
  title: string;
  startTime: string;
  endTime: string;
  color?: string;
  notes?: string;
  description?: string;
  location?: string;
  status?: string;
  businessName?: string;
  jobName?: string;
  groupId?: string;
  groupMessageId?: string;
  groupName?: string;
  sharedBy?: string;
}

interface Booking {
  id: string;
  startTime: string;
  endTime: string;
  status: string;
  service?: { name: string };
  staff?: { firstName: string; lastName: string };
}

const Calendar: React.FC = () => {
  const { user: authUser } = useAuth();
  const [activities, setActivities] = useState<PersonalActivity[]>([]);
  const [calendarItems, setCalendarItems] = useState<CalendarItem[]>([]);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [activeSession, setActiveSession] = useState<{ activityId: string; sessionId: string; startTime: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [range, setRange] = useState<'upcoming' | 'month' | 'next'>('upcoming');
  const [showCreateActivity, setShowCreateActivity] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createFreq, setCreateFreq] = useState<'daily' | 'weekly' | 'monthly'>('daily');
  const [createDuration, setCreateDuration] = useState(15);
  const [creating, setCreating] = useState(false);
  const [showStartModal, setShowStartModal] = useState<string | null>(null);
  const [reminderMinutes, setReminderMinutes] = useState<number | undefined>(60);
  const [showStopModal, setShowStopModal] = useState<string | null>(null);
  const [stopNotes, setStopNotes] = useState('');
  const [showSharedBooking, setShowSharedBooking] = useState(false);
  const [connections, setConnections] = useState<any[]>([]);
  const [sharedTitle, setSharedTitle] = useState('');
  const [sharedInviteeId, setSharedInviteeId] = useState('');
  const [sharedStart, setSharedStart] = useState('');
  const [sharedEnd, setSharedEnd] = useState('');
  const [sharedCreating, setSharedCreating] = useState(false);
  const [sharedBookings, setSharedBookings] = useState<any[]>([]);
  const [availabilityResult, setAvailabilityResult] = useState<{ available: boolean; conflicts: { type: string; title: string; start: string; end: string }[] } | null>(null);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [showCreateEvent, setShowCreateEvent] = useState(false);
  const [eventTitle, setEventTitle] = useState('');
  const [eventStart, setEventStart] = useState('');
  const [eventEnd, setEventEnd] = useState('');
  const [eventCreating, setEventCreating] = useState(false);
  const [showLogSession, setShowLogSession] = useState<string | null>(null);
  const [logStart, setLogStart] = useState('');
  const [logEnd, setLogEnd] = useState('');
  const [logNotes, setLogNotes] = useState('');
  const [logCreating, setLogCreating] = useState(false);
  const [filterShowPersonalEvents, setFilterShowPersonalEvents] = useState(true);
  const [filterShowActivitySessions, setFilterShowActivitySessions] = useState(true);
  const [filterShowSharedBookings, setFilterShowSharedBookings] = useState(true);
  const [filterShowBusiness, setFilterShowBusiness] = useState(true);
  const [filterShowBookings, setFilterShowBookings] = useState(true);
  const [filterShowHangouts, setFilterShowHangouts] = useState(true);
  const [filterShowChatInvites, setFilterShowChatInvites] = useState(true);
  const [acceptingInviteId, setAcceptingInviteId] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [showCreateHangout, setShowCreateHangout] = useState(false);
  const [showRequests, setShowRequests] = useState(false);
  const [hangoutInvites, setHangoutInvites] = useState<any[]>([]);
  const [hangoutTitle, setHangoutTitle] = useState('');
  const [hangoutMessage, setHangoutMessage] = useState('');
  const [hangoutSlots, setHangoutSlots] = useState<{ startTime: string; endTime: string }[]>([{ startTime: '', endTime: '' }]);
  const [hangoutInviteeIds, setHangoutInviteeIds] = useState<string[]>([]);
  const [hangoutCreating, setHangoutCreating] = useState(false);
  const [hangoutError, setHangoutError] = useState<string | null>(null);
  const [hangoutSuccess, setHangoutSuccess] = useState<string | null>(null);
  const [hangoutActionId, setHangoutActionId] = useState<string | null>(null);
  const [requestsTab, setRequestsTab] = useState<'incoming' | 'outgoing'>('incoming');

  const getDateRange = useCallback(() => {
    const now = new Date();
    let start: Date, end: Date;
    if (range === 'upcoming') {
      start = now;
      end = new Date(now);
      end.setMonth(end.getMonth() + 2);
    } else if (range === 'month') {
      start = new Date(now.getFullYear(), now.getMonth(), 1);
      end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    } else {
      start = new Date(now.getFullYear(), now.getMonth() + 1, 1);
      end = new Date(now.getFullYear(), now.getMonth() + 2, 0);
    }
    return {
      startDate: start.toISOString().split('T')[0],
      endDate: end.toISOString().split('T')[0],
    };
  }, [range]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { startDate, endDate } = getDateRange();
    try {
      const [actRes, calRes, bookRes, sharedRes, hangoutRes] = await Promise.all([
        personalActivitiesApi.list(),
        personalCalendarApi.getEvents({ startDate, endDate }),
        bookingsApi.list({ startDate, endDate, limit: 50 }),
        personalCalendarApi.listSharedBookings({ startDate, endDate }),
        hangoutInvitesApi.list(),
      ]);
      if (actRes.success && actRes.data) setActivities(actRes.data);
      else setActivities([]);
      if (calRes.success && calRes.data) setCalendarItems(calRes.data);
      else setCalendarItems([]);
      const b = bookRes.bookings || bookRes.data?.bookings || [];
      setBookings(b);
      setSharedBookings(sharedRes?.success && sharedRes?.data ? sharedRes.data : []);
      setHangoutInvites(hangoutRes?.success && hangoutRes?.data ? hangoutRes.data : []);
    } catch (err) {
      console.error('Failed to load:', err);
      setError('Could not load calendar. Try again.');
    } finally {
      setLoading(false);
    }
  }, [getDateRange]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  useEffect(() => {
    if (showSharedBooking || showCreateHangout) {
      connectionsApi.getConnections(1).then((res: any) => {
        if (res.success && res.data) setConnections(res.data.filter((c: any) => c.userId));
        else setConnections([]);
      });
    }
  }, [showSharedBooking, showCreateHangout]);

  useEffect(() => {
    if (showCreateHangout) setHangoutError(null);
    if (showRequests) setHangoutError(null);
  }, [showCreateHangout, showRequests]);

  const handleStartClick = (activityId: string) => {
    setShowStartModal(activityId);
    setReminderMinutes(60);
  };

  const handleStartConfirm = async () => {
    if (!showStartModal) return;
    try {
      const res = await personalActivitiesApi.start(showStartModal, reminderMinutes);
      if (res.success && res.data) {
        setActiveSession({
          activityId: showStartModal,
          sessionId: res.data.id,
          startTime: res.data.startTime,
        });
        setShowStartModal(null);
      }
    } catch (e) {
      console.error('Start failed:', e);
    }
  };

  const handleStopClick = (activityId: string) => {
    setShowStopModal(activityId);
    setStopNotes('');
  };

  const handleStopConfirm = async () => {
    if (!showStopModal || !activeSession || activeSession.activityId !== showStopModal) return;
    try {
      await personalActivitiesApi.stop(showStopModal, stopNotes, activeSession.sessionId);
      setActiveSession(null);
      setShowStopModal(null);
      setStopNotes('');
      loadAll();
    } catch (e) {
      console.error('Stop failed:', e);
    }
  };

  const handleCheckAvailability = async () => {
    if (!sharedInviteeId || !sharedStart || !sharedEnd) return;
    setAvailabilityLoading(true);
    setAvailabilityResult(null);
    try {
      const res = await personalCalendarApi.checkAvailability(sharedInviteeId, sharedStart, sharedEnd);
      const data = res as any;
      setAvailabilityResult({
        available: data?.available !== false,
        conflicts: Array.isArray(data?.conflicts) ? data.conflicts : [],
      });
    } catch {
      setAvailabilityResult({ available: false, conflicts: [] });
    } finally {
      setAvailabilityLoading(false);
    }
  };

  const handleCreateSharedBooking = async () => {
    if (!sharedTitle.trim() || !sharedInviteeId || !sharedStart || !sharedEnd) return;
    setSharedCreating(true);
    try {
      const res = await personalCalendarApi.createSharedBooking({
        inviteeId: sharedInviteeId,
        title: sharedTitle.trim(),
        startTime: sharedStart,
        endTime: sharedEnd,
      });
      if (res.success) {
        setShowSharedBooking(false);
        setSharedTitle('');
        setSharedInviteeId('');
        setSharedStart('');
        setSharedEnd('');
        setAvailabilityResult(null);
        loadAll();
      } else {
        showToast((res as any).error || 'Failed to create', 'error');
      }
    } catch (e: any) {
      showToast(e.message || 'Failed to create', 'error');
    } finally {
      setSharedCreating(false);
    }
  };

  const handleAcceptDecline = async (bookingId: string, status: 'accepted' | 'declined') => {
    try {
      await personalCalendarApi.updateSharedBooking(bookingId, status);
      loadAll();
    } catch (e) {
      console.error('Update failed:', e);
    }
  };

  const handleCreateEvent = async () => {
    if (!eventTitle.trim() || !eventStart) return;
    setEventCreating(true);
    try {
      const res = await personalCalendarApi.createEvent({
        title: eventTitle.trim(),
        startTime: eventStart,
        endTime: eventEnd || undefined,
      });
      if (res.success) {
        setShowCreateEvent(false);
        setEventTitle('');
        setEventStart('');
        setEventEnd('');
        loadAll();
      }
    } catch (e) {
      console.error('Create event failed:', e);
    } finally {
      setEventCreating(false);
    }
  };

  const handleLogSession = async () => {
    if (!showLogSession || !logStart) return;
    setLogCreating(true);
    try {
      const res = await personalActivitiesApi.logSession(showLogSession, {
        startTime: logStart,
        endTime: logEnd || undefined,
        notes: logNotes.trim() || undefined,
      });
      if (res.success) {
        setShowLogSession(null);
        setLogStart('');
        setLogEnd('');
        setLogNotes('');
        loadAll();
      }
    } catch (e) {
      console.error('Log session failed:', e);
    } finally {
      setLogCreating(false);
    }
  };

  const getSmartSlotDefaults = useCallback(() => {
    const now = new Date();
    const slots: { startTime: string; endTime: string }[] = [];
    const day = now.getDay();
    const daysUntilSat = day === 6 ? 0 : (6 - day + 7) % 7;
    for (let w = 0; w < 3; w++) {
      const sat = new Date(now);
      sat.setDate(now.getDate() + daysUntilSat + w * 7);
      sat.setHours(18, 0, 0, 0);
      const endSat = new Date(sat);
      endSat.setHours(21, 0, 0, 0);
      if (sat > now) {
        slots.push({
          startTime: sat.toISOString().slice(0, 16),
          endTime: endSat.toISOString().slice(0, 16),
        });
      }
      const sun = new Date(sat);
      sun.setDate(sat.getDate() + 1);
      sun.setHours(18, 0, 0, 0);
      const endSun = new Date(sun);
      endSun.setHours(21, 0, 0, 0);
      if (sun > now) {
        slots.push({
          startTime: sun.toISOString().slice(0, 16),
          endTime: endSun.toISOString().slice(0, 16),
        });
      }
    }
    return slots.slice(0, 4).length ? slots.slice(0, 4) : [{ startTime: '', endTime: '' }];
  }, []);

  const validateHangoutSlots = (slots: { startTime: string; endTime: string }[]) => {
    const now = new Date();
    const valid = slots.filter((s) => s.startTime && s.endTime);
    for (const s of valid) {
      const start = new Date(s.startTime);
      const end = new Date(s.endTime);
      if (end.getTime() <= start.getTime()) return 'End time must be after start time';
      if (start < now) return 'Cannot add slots in the past';
      const daysDiff = (start.getTime() - now.getTime()) / (24 * 60 * 60 * 1000);
      if (daysDiff > 120) return 'Slots must be within the next 120 days';
    }
    for (let i = 0; i < valid.length; i++) {
      for (let j = i + 1; j < valid.length; j++) {
        const a = new Date(valid[i].startTime);
        const b = new Date(valid[i].endTime);
        const c = new Date(valid[j].startTime);
        const d = new Date(valid[j].endTime);
        if (a < d && c < b) return 'Some slots overlap — please adjust';
      }
    }
    return null;
  };

  const handleCreateHangout = async () => {
    const slots = hangoutSlots.filter((s) => s.startTime && s.endTime);
    if (!slots.length || hangoutInviteeIds.length === 0) return;
    setHangoutError(null);
    const validationErr = validateHangoutSlots(slots);
    if (validationErr) {
      setHangoutError(validationErr);
      return;
    }
    setHangoutCreating(true);
    setHangoutError(null);
    try {
      const res = await hangoutInvitesApi.create({
        title: hangoutTitle.trim() || 'Catch up',
        message: hangoutMessage.trim() || undefined,
        proposedSlots: slots.map((s) => ({ startTime: s.startTime, endTime: s.endTime })),
        inviteeIds: hangoutInviteeIds,
      });
      if (res.success) {
        setHangoutSuccess('Invite sent!');
        setTimeout(() => {
          setShowCreateHangout(false);
          setHangoutTitle('');
          setHangoutMessage('');
          setHangoutSlots([{ startTime: '', endTime: '' }]);
          setHangoutInviteeIds([]);
          setHangoutSuccess(null);
          loadAll();
        }, 800);
      } else {
        setHangoutError((res as any).error || 'Failed to create');
      }
    } catch (e: any) {
      setHangoutError(e.message || 'Failed to create');
    } finally {
      setHangoutCreating(false);
    }
  };

  const handleHangoutAccept = async (id: string, slotIndex: number) => {
    setHangoutActionId(id);
    setHangoutError(null);
    try {
      const res = await hangoutInvitesApi.accept(id, { slotIndex });
      if (res.success) {
        setHangoutSuccess('You\'re in!');
        setTimeout(() => { setHangoutSuccess(null); loadAll(); }, 600);
      } else {
        setHangoutError((res as any).error || 'Failed to accept');
      }
    } catch (e) {
      setHangoutError('Failed to accept');
    } finally {
      setHangoutActionId(null);
    }
  };

  const handleHangoutDismiss = async (id: string) => {
    setHangoutActionId(id);
    setHangoutError(null);
    try {
      const res = await hangoutInvitesApi.dismiss(id);
      if (res.success) {
        setHangoutSuccess('Declined');
        setTimeout(() => { setHangoutSuccess(null); loadAll(); }, 600);
      } else {
        setHangoutError((res as any).error || 'Failed to dismiss');
      }
    } catch (e) {
      setHangoutError('Failed to dismiss');
    } finally {
      setHangoutActionId(null);
    }
  };

  const handleHangoutCancel = async (id: string) => {
    setHangoutActionId(id);
    setHangoutError(null);
    try {
      const res = await hangoutInvitesApi.delete(id);
      if (res.success) {
        setHangoutSuccess('Cancelled');
        setTimeout(() => { setHangoutSuccess(null); loadAll(); }, 600);
      } else {
        setHangoutError((res as any).error || 'Failed to cancel');
      }
    } catch (e) {
      setHangoutError('Failed to cancel');
    } finally {
      setHangoutActionId(null);
    }
  };

  const handleCreateActivity = async () => {
    if (!createName.trim()) return;
    setCreating(true);
    try {
      const res = await personalActivitiesApi.create({
        name: createName.trim(),
        frequency: createFreq,
        durationMinutes: createDuration,
      });
      if (res.success) {
        setShowCreateActivity(false);
        setCreateName('');
        setCreateDuration(15);
        loadAll();
      }
    } catch (e) {
      console.error('Create failed:', e);
    } finally {
      setCreating(false);
    }
  };

  // "View in chat" deep link — hands off the current session to mango-chat via a
  // `#mango_token=` hash (never sent to a server, consumed on mount there). See shared/ui/hashToken.ts.
  const chatLinkUrl = (groupId: string) => {
    const token = localStorage.getItem('mangoPersonalToken');
    return `${MANGO_CHAT_URL}/group/${groupId}${token ? `#mango_token=${encodeURIComponent(token)}` : ''}`;
  };

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  };
  const formatTime = (iso: string) => new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });

  const filterCalendarItem = (c: CalendarItem) => {
    if (c.type === 'personal_event') return filterShowPersonalEvents;
    if (c.type === 'activity_session') return filterShowActivitySessions;
    if (c.type === 'shared_booking') return filterShowSharedBookings;
    if (c.type === 'hangout_invite') return filterShowHangouts;
    if (c.type === 'chat_event_invite' || c.type === 'chat_dinner_plan' || c.type === 'chat_find_a_time') return filterShowChatInvites;
    if (c.type === 'business_event' || c.type === 'staff_job_session') return filterShowBusiness;
    return true;
  };

  const handleAcceptChatEventInvite = async (item: CalendarItem) => {
    if (!item.groupId || !item.groupMessageId || acceptingInviteId) return;
    setAcceptingInviteId(item.id);
    try {
      const res = await groupsApi.eventRsvp(item.groupId, item.groupMessageId);
      if (res?.success) loadAll();
    } finally {
      setAcceptingInviteId(null);
    }
  };
  const allItems = [
    ...calendarItems
      .filter((c) => filterCalendarItem(c))
      .map((c) => ({
        ...c,
        sortKey: new Date(c.startTime).getTime(),
      })),
    ...(filterShowBookings ? bookings : []).map((b) => ({
      id: b.id,
      type: 'booking' as const,
      title: b.service?.name || 'Booking',
      startTime: b.startTime,
      endTime: b.endTime,
      color: '#ff6b35',
      status: b.status,
      sortKey: new Date(b.startTime).getTime(),
    })),
  ].sort((a, b) => a.sortKey - b.sortKey);

  return (
    <div style={{ maxWidth: '960px', margin: '0 auto' }}>
      <div style={{
        background: 'linear-gradient(135deg, #ff9a56 0%, #ff6b35 100%)',
        borderRadius: '16px',
        padding: '24px',
        marginBottom: '24px',
        color: 'white',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' }}>
          <div>
            <h1 style={{ margin: '0 0 8px 0', fontSize: '28px' }}>📅 Unified Calendar</h1>
            <p style={{ margin: 0, opacity: 0.95, fontSize: '15px' }}>
              Activities, events, bookings — all in one place
            </p>
          </div>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button type="button" onClick={() => setShowCreateHangout(true)} style={{ padding: '8px 16px', background: 'rgba(255,255,255,0.3)', border: '1px solid rgba(255,255,255,0.5)', borderRadius: '8px', color: 'white', cursor: 'pointer', fontSize: '14px' }}>
              🎉 Plan hangout
            </button>
            <button type="button" onClick={() => setShowRequests(true)} style={{ padding: '8px 16px', background: 'rgba(255,255,255,0.3)', border: '1px solid rgba(255,255,255,0.5)', borderRadius: '8px', color: 'white', cursor: 'pointer', fontSize: '14px' }}>
              📩 Requests
            </button>
            <button type="button" onClick={() => setShowCreateEvent(true)} style={{ padding: '8px 16px', background: 'rgba(255,255,255,0.3)', border: '1px solid rgba(255,255,255,0.5)', borderRadius: '8px', color: 'white', cursor: 'pointer', fontSize: '14px' }}>
              + Event
            </button>
            <button type="button" onClick={loadAll} disabled={loading} style={{ padding: '8px 16px', background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.4)', borderRadius: '8px', color: 'white', cursor: loading ? 'not-allowed' : 'pointer', fontSize: '14px' }}>
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Requests modal - Hangout invites (incoming + outgoing) */}
      {showRequests && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1003 }} onClick={() => { setShowRequests(false); setHangoutError(null); setHangoutSuccess(null); }}>
          <div style={{ background: 'white', borderRadius: '16px', padding: '24px', width: '540px', maxWidth: '95vw', maxHeight: '85vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 16px 0' }}>📩 Hangout requests</h3>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
              <button type="button" onClick={() => setRequestsTab('incoming')} style={{ padding: '8px 16px', background: requestsTab === 'incoming' ? '#e0e7ff' : '#f3f4f6', color: requestsTab === 'incoming' ? '#4338ca' : '#666', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: requestsTab === 'incoming' ? 600 : 400 }}>
                Incoming
              </button>
              <button type="button" onClick={() => setRequestsTab('outgoing')} style={{ padding: '8px 16px', background: requestsTab === 'outgoing' ? '#e0e7ff' : '#f3f4f6', color: requestsTab === 'outgoing' ? '#4338ca' : '#666', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: requestsTab === 'outgoing' ? 600 : 400 }}>
                Outgoing
              </button>
            </div>
            {hangoutError && <div style={{ padding: '10px 14px', background: '#fee2e2', color: '#991b1b', borderRadius: '8px', marginBottom: '12px', fontSize: '14px' }}>{hangoutError}</div>}
            {hangoutSuccess && <div style={{ padding: '10px 14px', background: '#d1fae5', color: '#065f46', borderRadius: '8px', marginBottom: '12px', fontSize: '14px' }}>✓ {hangoutSuccess}</div>}
            {(() => {
              const incoming = hangoutInvites.filter((h: any) => h.initiatorId !== authUser?.id);
              const outgoing = hangoutInvites.filter((h: any) => h.initiatorId === authUser?.id);
              const list = requestsTab === 'incoming' ? incoming : outgoing;
              if (list.length === 0) {
                return (
                  <p style={{ color: '#666', fontSize: '14px', padding: '24px 0' }}>
                    {requestsTab === 'incoming' ? 'No hangout invites yet. When friends invite you, they\'ll appear here.' : 'You haven\'t sent any hangout invites. Hit "Plan hangout" to invite friends.'}
                  </p>
                );
              }
              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {list.map((h: any) => {
                    const myResponse = h.responses?.find((r: any) => r.inviteeId === authUser?.id);
                    const isInitiator = h.initiatorId === authUser?.id;
                    const slots = (h.proposedSlots || []) as { startTime: string; endTime: string }[];
                    const acceptedResponses = h.responses?.filter((r: any) => r.status === 'accepted') || [];
                    const loading = hangoutActionId === h.id;
                    return (
                      <div key={h.id} style={{ padding: '16px', background: '#f9fafb', borderRadius: '12px', border: '1px solid #e5e7eb' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '8px' }}>
                          <div>
                            <strong>{h.title || 'Catch up'}</strong>
                            {!isInitiator && <span style={{ fontSize: '13px', color: '#666', marginLeft: '8px' }}>— {h.initiator?.firstName} {h.initiator?.lastName}</span>}
                            {h.status === 'confirmed' && <span style={{ marginLeft: '8px', fontSize: '12px', background: '#d1fae5', color: '#065f46', padding: '2px 8px', borderRadius: '6px' }}>Confirmed</span>}
                            {h.status === 'pending' && isInitiator && <span style={{ marginLeft: '8px', fontSize: '12px', background: '#fef3c7', color: '#92400e', padding: '2px 8px', borderRadius: '6px' }}>Waiting</span>}
                          </div>
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            {h.status === 'cancelled' && <span style={{ fontSize: '12px', color: '#999' }}>Cancelled</span>}
                            {h.status === 'pending' && isInitiator && (
                              <button type="button" onClick={() => handleHangoutCancel(h.id)} disabled={loading} style={{ padding: '6px 12px', background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: '8px', cursor: loading ? 'not-allowed' : 'pointer', fontSize: '13px' }}>
                                {loading ? '…' : 'Cancel'}
                              </button>
                            )}
                          </div>
                        </div>
                        {h.confirmedSlot && (
                          <p style={{ margin: '8px 0 0 0', fontSize: '14px', color: '#333' }}>
                            ✓ {formatDate((h.confirmedSlot as any).startTime)} · {formatTime((h.confirmedSlot as any).startTime)} – {formatTime((h.confirmedSlot as any).endTime)}
                          </p>
                        )}
                        {acceptedResponses.length > 0 && (
                          <p style={{ margin: '6px 0 0 0', fontSize: '13px', color: '#059669' }}>
                            {acceptedResponses.map((r: any) => `${r.invitee?.firstName || 'Someone'} accepted`).join(' · ')}
                            {h.confirmedSlot && acceptedResponses[0]?.acceptedSlot && ` — ${formatDate((acceptedResponses[0].acceptedSlot as any)?.startTime || (h.confirmedSlot as any).startTime)} ${formatTime((acceptedResponses[0].acceptedSlot as any)?.startTime || (h.confirmedSlot as any).startTime)}`}
                          </p>
                        )}
                        {h.status === 'pending' && slots.length > 0 && !isInitiator && myResponse?.status === 'pending' && (
                          <div style={{ marginTop: '12px' }}>
                            <p style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#555' }}>These times work for {h.initiator?.firstName}:</p>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
                              {slots.map((s, i) => (
                                <button key={i} type="button" onClick={() => handleHangoutAccept(h.id, i)} disabled={loading} style={{ padding: '6px 12px', background: loading ? '#9ca3af' : '#4caf50', color: 'white', border: 'none', borderRadius: '8px', cursor: loading ? 'not-allowed' : 'pointer', fontSize: '13px' }}>
                                  {formatDate(s.startTime)} {formatTime(s.startTime)}
                                </button>
                              ))}
                            </div>
                            <button type="button" onClick={() => handleHangoutDismiss(h.id)} disabled={loading} style={{ padding: '6px 12px', background: loading ? '#9ca3af' : '#f44336', color: 'white', border: 'none', borderRadius: '8px', cursor: loading ? 'not-allowed' : 'pointer', fontSize: '13px' }}>
                              {loading ? '…' : 'Dismiss'}
                            </button>
                          </div>
                        )}
                        {h.status === 'pending' && isInitiator && acceptedResponses.length === 0 && (
                          <p style={{ margin: '8px 0 0 0', fontSize: '13px', color: '#666' }}>
                            {h.responses?.filter((r: any) => r.status === 'accepted').length || 0} accepted · {h.responses?.filter((r: any) => r.status === 'declined').length || 0} declined
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })()}
            <div style={{ marginTop: '16px' }}>
              <button type="button" onClick={() => { setShowRequests(false); setHangoutError(null); setHangoutSuccess(null); }} style={{ padding: '10px 20px', background: '#eee', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Create hangout modal */}
      {showCreateHangout && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1003 }} onClick={() => { if (!hangoutCreating) { setShowCreateHangout(false); setHangoutError(null); } }}>
          <div style={{ background: 'white', borderRadius: '16px', padding: '24px', width: '480px', maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 16px 0' }}>🎉 Plan hangout</h3>
            <p style={{ margin: '0 0 16px 0', fontSize: '14px', color: '#666' }}>Share your free times with friends. Hangout is created when you + 1 friend agree.</p>
            {hangoutError && <div style={{ padding: '10px 14px', background: '#fee2e2', color: '#991b1b', borderRadius: '8px', marginBottom: '12px', fontSize: '14px' }}>{hangoutError}</div>}
            {hangoutSuccess && <div style={{ padding: '10px 14px', background: '#d1fae5', color: '#065f46', borderRadius: '8px', marginBottom: '12px', fontSize: '14px' }}>✓ {hangoutSuccess}</div>}
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', color: '#555' }}>Title</label>
              <input type="text" value={hangoutTitle} onChange={(e) => setHangoutTitle(e.target.value)} placeholder="e.g. Games & dinner night" style={{ width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px' }} />
            </div>
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', color: '#555' }}>Message (optional)</label>
              <input type="text" value={hangoutMessage} onChange={(e) => setHangoutMessage(e.target.value)} placeholder="Add a note for your friends" style={{ width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px' }} />
            </div>
            <div style={{ marginBottom: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                <label style={{ fontSize: '14px', color: '#555' }}>Your free times *</label>
                <button type="button" onClick={() => setHangoutSlots(getSmartSlotDefaults())} style={{ padding: '4px 10px', background: '#e0e7ff', color: '#4338ca', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}>Suggest weekend evenings</button>
              </div>
              {hangoutSlots.map((s, i) => (
                <div key={i} style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
                  <input type="datetime-local" value={s.startTime} onChange={(e) => { const n = [...hangoutSlots]; n[i] = { ...n[i], startTime: e.target.value }; setHangoutSlots(n); }} style={{ flex: 1, padding: '8px 10px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '13px' }} />
                  <input type="datetime-local" value={s.endTime} onChange={(e) => { const n = [...hangoutSlots]; n[i] = { ...n[i], endTime: e.target.value }; setHangoutSlots(n); }} style={{ flex: 1, padding: '8px 10px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '13px' }} />
                  {hangoutSlots.length > 1 && <button type="button" onClick={() => setHangoutSlots(hangoutSlots.filter((_, j) => j !== i))} style={{ padding: '6px 10px', background: '#fee', color: '#c00', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}>−</button>}
                </div>
              ))}
              <button type="button" onClick={() => setHangoutSlots([...hangoutSlots, { startTime: '', endTime: '' }])} style={{ padding: '6px 14px', background: '#e0e7ff', color: '#4338ca', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' }}>+ Add slot</button>
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', color: '#555' }}>Invite friends *</label>
              <div style={{ maxHeight: '140px', overflowY: 'auto', border: '1px solid #ddd', borderRadius: '8px', padding: '8px' }}>
                {connections.map((c: any) => (
                  <label key={c.userId} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 0', cursor: 'pointer', fontSize: '14px' }}>
                    <input type="checkbox" checked={hangoutInviteeIds.includes(c.userId)} onChange={(e) => { if (e.target.checked) setHangoutInviteeIds([...hangoutInviteeIds, c.userId]); else setHangoutInviteeIds(hangoutInviteeIds.filter((id) => id !== c.userId)); }} />
                    {c.userName || c.userId}
                  </label>
                ))}
              </div>
              {connections.length === 0 && <p style={{ margin: '8px 0 0 0', fontSize: '13px', color: '#999' }}>Add connections first to invite them.</p>}
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setShowCreateHangout(false)} disabled={hangoutCreating} style={{ padding: '10px 20px', background: '#eee', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>Cancel</button>
              <button type="button" onClick={handleCreateHangout} disabled={hangoutCreating || !hangoutSlots.some((s) => s.startTime && s.endTime) || hangoutInviteeIds.length === 0} style={{ padding: '10px 20px', background: '#10b981', color: 'white', border: 'none', borderRadius: '8px', cursor: hangoutCreating ? 'not-allowed' : 'pointer' }}>
                {hangoutCreating ? 'Sending…' : 'Send invite'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pending shared booking invites (where we're the invitee) */}
      {authUser && sharedBookings.filter((b: any) => b.status === 'pending' && b.inviteeId === authUser.id).length > 0 && (
        <div style={{ background: '#fff3cd', borderRadius: '12px', padding: '16px', marginBottom: '20px', border: '1px solid #ffc107' }}>
          <h3 style={{ margin: '0 0 12px 0', fontSize: '16px' }}>📩 Pending invites</h3>
          {sharedBookings
            .filter((b: any) => b.status === 'pending' && b.inviteeId === authUser.id)
            .map((b: any) => (
              <div key={b.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px', padding: '10px 0', borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
                <div>
                  <strong>{b.title}</strong> — {b.creator?.firstName} {b.creator?.lastName}
                  <br />
                  <span style={{ fontSize: '13px', color: '#666' }}>
                    {formatDate(b.startTime)} · {formatTime(b.startTime)} – {formatTime(b.endTime)}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button type="button" onClick={() => handleAcceptDecline(b.id, 'accepted')} style={{ padding: '6px 14px', background: '#4caf50', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 }}>
                    Accept
                  </button>
                  <button type="button" onClick={() => handleAcceptDecline(b.id, 'declined')} style={{ padding: '6px 14px', background: '#f44336', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>
                    Decline
                  </button>
                </div>
              </div>
            ))}
        </div>
      )}

      {/* Activity tracking - Start/Stop */}
      <div style={{ background: 'white', borderRadius: '16px', padding: '20px', marginBottom: '24px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
        <h2 style={{ margin: '0 0 16px 0', fontSize: '18px', color: '#333' }}>🕐 Track time</h2>
        <p style={{ margin: '0 0 16px 0', fontSize: '14px', color: '#666' }}>
          Pick an activity, press Start, then Stop when done. Add comments after stopping.
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '12px' }}>
          {activities.filter((a) => a.isActive).map((a) => (
            <div
              key={a.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '10px 16px',
                background: activeSession?.activityId === a.id ? '#e8f5e9' : '#f5f5f5',
                borderRadius: '10px',
                border: activeSession?.activityId === a.id ? '2px solid #4caf50' : '1px solid #eee',
              }}
            >
              <div style={{ width: '12px', height: '12px', borderRadius: '4px', background: a.color || '#ff6b35' }} />
              <span style={{ fontWeight: 500, color: '#333' }}>{a.name}</span>
              <span style={{ fontSize: '12px', color: '#888' }}>
                {a.durationMinutes} min/{a.frequency}
              </span>
              {activeSession?.activityId === a.id ? (
                <button
                  type="button"
                  onClick={() => handleStopClick(a.id)}
                  style={{ padding: '6px 14px', background: '#f44336', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '13px' }}
                >
                  Stop
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => handleStartClick(a.id)}
                    disabled={!!activeSession}
                    style={{ padding: '6px 14px', background: activeSession ? '#ccc' : '#4caf50', color: 'white', border: 'none', borderRadius: '8px', cursor: activeSession ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: '13px' }}
                  >
                    Start
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowLogSession(a.id); setLogStart(''); setLogEnd(''); setLogNotes(''); }}
                    disabled={!!activeSession}
                    style={{ padding: '6px 12px', background: '#eee', color: '#666', border: '1px solid #ddd', borderRadius: '8px', cursor: activeSession ? 'not-allowed' : 'pointer', fontSize: '12px' }}
                  >
                    Log
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={() => setShowCreateActivity(true)}
            style={{ padding: '8px 16px', background: '#ff6b35', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' }}
          >
            + Add activity
          </button>
          <button
            type="button"
            onClick={() => setShowSharedBooking(true)}
            style={{ padding: '8px 16px', background: '#10b981', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' }}
          >
            📅 Meet with friend
          </button>
        </div>
      </div>

      {/* Start modal - reminder option */}
      {showStartModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setShowStartModal(null)}>
          <div style={{ background: 'white', borderRadius: '16px', padding: '24px', width: '340px', maxWidth: '90vw' }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 16px 0' }}>Remind me</h3>
            <p style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#666' }}>Optional: get a reminder to stop (e.g. if you want to cap your session)</p>
            <select value={reminderMinutes ?? ''} onChange={(e) => setReminderMinutes(e.target.value ? parseInt(e.target.value, 10) : undefined)} style={{ width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px', marginBottom: '16px' }}>
              <option value="">No reminder</option>
              <option value="15">15 minutes</option>
              <option value="30">30 minutes</option>
              <option value="60">1 hour</option>
              <option value="120">2 hours</option>
              <option value="240">4 hours</option>
            </select>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setShowStartModal(null)} style={{ padding: '10px 20px', background: '#eee', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>Cancel</button>
              <button type="button" onClick={handleStartConfirm} style={{ padding: '10px 20px', background: '#4caf50', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>Start</button>
            </div>
          </div>
        </div>
      )}

      {/* Stop modal - add notes */}
      {showStopModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1001 }} onClick={() => setShowStopModal(null)}>
          <div style={{ background: 'white', borderRadius: '16px', padding: '24px', width: '380px', maxWidth: '90vw' }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 16px 0' }}>Add notes</h3>
            <p style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#666' }}>Optional: add comments before stopping</p>
            <textarea value={stopNotes} onChange={(e) => setStopNotes(e.target.value)} placeholder="What did you do? How did it go?" rows={3} style={{ width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px', resize: 'vertical', marginBottom: '16px' }} />
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setShowStopModal(null)} style={{ padding: '10px 20px', background: '#eee', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>Cancel</button>
              <button type="button" onClick={handleStopConfirm} style={{ padding: '10px 20px', background: '#f44336', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>Stop</button>
            </div>
          </div>
        </div>
      )}

      {/* Shared booking modal */}
      {showSharedBooking && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1002 }} onClick={() => !sharedCreating && (setShowSharedBooking(false), setAvailabilityResult(null))}>
          <div style={{ background: 'white', borderRadius: '16px', padding: '24px', width: '420px', maxWidth: '90vw', maxHeight: '90vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 16px 0' }}>Meet with friend</h3>
            <p style={{ margin: '0 0 16px 0', fontSize: '14px', color: '#666' }}>Create a booking and invite a Mango connection. Check their availability before sending.</p>
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', color: '#555' }}>Invite</label>
              <select value={sharedInviteeId} onChange={(e) => { setSharedInviteeId(e.target.value); setAvailabilityResult(null); }} style={{ width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px' }}>
                <option value="">Select connection…</option>
                {connections.map((c) => (
                  <option key={c.userId} value={c.userId}>{c.userName || c.userId}</option>
                ))}
              </select>
            </div>
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', color: '#555' }}>Title</label>
              <input type="text" value={sharedTitle} onChange={(e) => setSharedTitle(e.target.value)} placeholder="e.g. Coffee catch-up" style={{ width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px' }} />
            </div>
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', color: '#555' }}>Start</label>
              <input type="datetime-local" value={sharedStart} onChange={(e) => { setSharedStart(e.target.value); setAvailabilityResult(null); }} style={{ width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px' }} />
            </div>
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', color: '#555' }}>End</label>
              <input type="datetime-local" value={sharedEnd} onChange={(e) => { setSharedEnd(e.target.value); setAvailabilityResult(null); }} style={{ width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px' }} />
            </div>
            {sharedInviteeId && sharedStart && sharedEnd && (
              <div style={{ marginBottom: '16px' }}>
                <button type="button" onClick={handleCheckAvailability} disabled={availabilityLoading} style={{ padding: '8px 16px', background: '#e0e7ff', color: '#4338ca', border: '1px solid #c7d2fe', borderRadius: '8px', cursor: availabilityLoading ? 'not-allowed' : 'pointer', fontSize: '13px' }}>
                  {availabilityLoading ? 'Checking…' : 'Check availability'}
                </button>
                {availabilityResult && (
                  <div style={{ marginTop: '12px', padding: '12px', borderRadius: '8px', background: availabilityResult.available ? '#d1fae5' : '#fee2e2', border: `1px solid ${availabilityResult.available ? '#a7f3d0' : '#fecaca'}` }}>
                    <p style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: 600, color: availabilityResult.available ? '#065f46' : '#991b1b' }}>
                      {availabilityResult.available ? '✓ Available' : '✗ Conflicts found'}
                    </p>
                    {availabilityResult.conflicts.length > 0 && (
                      <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '13px', color: '#7f1d1d' }}>
                        {availabilityResult.conflicts.map((c, i) => (
                          <li key={i}>
                            {c.title} ({c.type}) — {new Date(c.start).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })} – {new Date(c.end).toLocaleString(undefined, { timeStyle: 'short' })}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            )}
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => { setShowSharedBooking(false); setAvailabilityResult(null); }} disabled={sharedCreating} style={{ padding: '10px 20px', background: '#eee', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>Cancel</button>
              <button type="button" onClick={handleCreateSharedBooking} disabled={sharedCreating || !sharedTitle.trim() || !sharedInviteeId || !sharedStart || !sharedEnd} style={{ padding: '10px 20px', background: '#10b981', color: 'white', border: 'none', borderRadius: '8px', cursor: sharedCreating ? 'not-allowed' : 'pointer' }}>
                {sharedCreating ? 'Creating…' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create event modal */}
      {showCreateEvent && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1002 }} onClick={() => !eventCreating && setShowCreateEvent(false)}>
          <div style={{ background: 'white', borderRadius: '16px', padding: '24px', width: '400px', maxWidth: '90vw' }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 16px 0' }}>Create event</h3>
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', color: '#555' }}>Title *</label>
              <input type="text" value={eventTitle} onChange={(e) => setEventTitle(e.target.value)} placeholder="e.g. Team meeting" style={{ width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px' }} />
            </div>
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', color: '#555' }}>Start *</label>
              <input type="datetime-local" value={eventStart} onChange={(e) => setEventStart(e.target.value)} style={{ width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px' }} />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', color: '#555' }}>End</label>
              <input type="datetime-local" value={eventEnd} onChange={(e) => setEventEnd(e.target.value)} style={{ width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px' }} />
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setShowCreateEvent(false)} disabled={eventCreating} style={{ padding: '10px 20px', background: '#eee', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>Cancel</button>
              <button type="button" onClick={handleCreateEvent} disabled={eventCreating || !eventTitle.trim() || !eventStart} style={{ padding: '10px 20px', background: '#8b5cf6', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>{eventCreating ? 'Creating…' : 'Create'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Log past session modal */}
      {showLogSession && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1002 }} onClick={() => !logCreating && setShowLogSession(null)}>
          <div style={{ background: 'white', borderRadius: '16px', padding: '24px', width: '400px', maxWidth: '90vw' }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 16px 0' }}>Log past session</h3>
            <p style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#666' }}>Add a session you forgot to track</p>
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', color: '#555' }}>Start time *</label>
              <input type="datetime-local" value={logStart} onChange={(e) => setLogStart(e.target.value)} style={{ width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px' }} />
            </div>
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', color: '#555' }}>End time</label>
              <input type="datetime-local" value={logEnd} onChange={(e) => setLogEnd(e.target.value)} style={{ width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px' }} />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', color: '#555' }}>Notes</label>
              <textarea value={logNotes} onChange={(e) => setLogNotes(e.target.value)} placeholder="Optional" rows={2} style={{ width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px', resize: 'vertical' }} />
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setShowLogSession(null)} disabled={logCreating} style={{ padding: '10px 20px', background: '#eee', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>Cancel</button>
              <button type="button" onClick={handleLogSession} disabled={logCreating || !logStart} style={{ padding: '10px 20px', background: '#ff6b35', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>{logCreating ? 'Adding…' : 'Add'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Create activity modal */}
      {showCreateActivity && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => !creating && setShowCreateActivity(false)}>
          <div style={{ background: 'white', borderRadius: '16px', padding: '24px', width: '340px', maxWidth: '90vw' }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 16px 0' }}>Create activity</h3>
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', color: '#555' }}>Name</label>
              <input
                type="text"
                value={createName}
                onChange={(e) => setCreateName(e.target.value)}
                placeholder="e.g. Yoga, Working on Mango"
                style={{ width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px' }}
              />
            </div>
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', color: '#555' }}>Frequency</label>
              <select value={createFreq} onChange={(e) => setCreateFreq(e.target.value as any)} style={{ width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px' }}>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', color: '#555' }}>Target minutes</label>
              <input
                type="number"
                value={createDuration}
                onChange={(e) => setCreateDuration(Math.max(1, parseInt(e.target.value, 10) || 15))}
                min={1}
                max={480}
                style={{ width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px' }}
              />
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setShowCreateActivity(false)} disabled={creating} style={{ padding: '10px 20px', background: '#eee', border: 'none', borderRadius: '8px', cursor: creating ? 'not-allowed' : 'pointer' }}>
                Cancel
              </button>
              <button type="button" onClick={handleCreateActivity} disabled={creating || !createName.trim()} style={{ padding: '10px 20px', background: '#ff6b35', color: 'white', border: 'none', borderRadius: '8px', cursor: creating || !createName.trim() ? 'not-allowed' : 'pointer' }}>
                {creating ? 'Creating…' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div style={{ marginBottom: '20px' }}>
        <button
          type="button"
          onClick={() => setShowFilters(!showFilters)}
          style={{
            padding: '8px 16px',
            background: showFilters ? '#e0e7ff' : '#f5f5f5',
            color: showFilters ? '#4338ca' : '#333',
            border: '1px solid #ddd',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '14px',
          }}
        >
          {showFilters ? '▼' : '▶'} Filters
        </button>
        {showFilters && (
          <div style={{ marginTop: '12px', padding: '16px', background: '#f9fafb', borderRadius: '12px', border: '1px solid #e5e7eb', display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px' }}>
              <input type="checkbox" checked={filterShowPersonalEvents} onChange={(e) => setFilterShowPersonalEvents(e.target.checked)} />
              Personal events
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px' }}>
              <input type="checkbox" checked={filterShowActivitySessions} onChange={(e) => setFilterShowActivitySessions(e.target.checked)} />
              Activity sessions
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px' }}>
              <input type="checkbox" checked={filterShowSharedBookings} onChange={(e) => setFilterShowSharedBookings(e.target.checked)} />
              Shared bookings
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px' }}>
              <input type="checkbox" checked={filterShowBusiness} onChange={(e) => setFilterShowBusiness(e.target.checked)} />
              Business (events & job sessions)
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px' }}>
              <input type="checkbox" checked={filterShowBookings} onChange={(e) => setFilterShowBookings(e.target.checked)} />
              Customer bookings
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px' }}>
              <input type="checkbox" checked={filterShowHangouts} onChange={(e) => setFilterShowHangouts(e.target.checked)} />
              Hangouts
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px' }}>
              <input type="checkbox" checked={filterShowChatInvites} onChange={(e) => setFilterShowChatInvites(e.target.checked)} />
              Chat event invites
            </label>
          </div>
        )}
      </div>

      {/* Date range tabs */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', flexWrap: 'wrap' }}>
        {[
          { id: 'upcoming' as const, label: 'Upcoming' },
          { id: 'month' as const, label: 'This month' },
          { id: 'next' as const, label: 'Next month' },
        ].map((r) => (
          <button
            key={r.id}
            onClick={() => setRange(r.id)}
            style={{
              padding: '10px 20px',
              background: range === r.id ? '#ff6b35' : 'white',
              color: range === r.id ? 'white' : '#333',
              border: range === r.id ? 'none' : '1px solid #ddd',
              borderRadius: '10px',
              cursor: 'pointer',
              fontWeight: range === r.id ? 600 : 400,
              fontSize: '14px',
            }}
          >
            {r.label}
          </button>
        ))}
      </div>

      {error && (
        <div style={{ padding: '16px', background: '#fee', borderRadius: '12px', marginBottom: '20px', color: '#c00' }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '48px', color: '#666' }}>Loading…</div>
      ) : allItems.length === 0 ? (
        <div style={{ background: 'white', borderRadius: '16px', padding: '48px', textAlign: 'center', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
          <span style={{ fontSize: '48px' }}>📅</span>
          <h3 style={{ margin: '16px 0 8px 0', color: '#333' }}>No events yet</h3>
          <p style={{ color: '#666', margin: 0 }}>
            Add activities, create events, or book services — they’ll all show here.
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {allItems.map((item) => (
            <div
              key={item.id}
              style={{
                background: 'white',
                borderRadius: '12px',
                padding: '20px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                borderLeft: `4px solid ${item.color || '#ff6b35'}`,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <h3 style={{ margin: 0, fontSize: '18px', color: '#333' }}>{item.title}</h3>
                    {(item as CalendarItem).businessName && (
                      <span style={{ fontSize: '12px', color: '#666', background: '#f0f0f0', padding: '2px 8px', borderRadius: '6px' }}>
                        {(item as CalendarItem).businessName}
                      </span>
                    )}
                  </div>
                  <p style={{ margin: 0, fontSize: '14px', color: '#666' }}>
                    {formatDate(item.startTime)} · {formatTime(item.startTime)} – {formatTime(item.endTime)}
                  </p>
                  {(item as CalendarItem).notes && <p style={{ margin: '6px 0 0 0', fontSize: '13px', color: '#666' }}>{(item as CalendarItem).notes}</p>}
                  {(item.type === 'chat_event_invite' || item.type === 'chat_dinner_plan' || item.type === 'chat_find_a_time') && (
                    <p style={{ margin: '6px 0 0 0', fontSize: '13px', color: '#666' }}>
                      Shared by {(item as CalendarItem).sharedBy} in {(item as CalendarItem).groupName}
                    </p>
                  )}
                  {(item as CalendarItem).groupId && (item as CalendarItem).groupMessageId && (
                    <a
                      href={chatLinkUrl((item as CalendarItem).groupId!)}
                      target="_blank"
                      rel="noreferrer"
                      style={{ display: 'inline-block', marginTop: '6px', fontSize: '13px', fontWeight: 600, color: '#ff6b35', textDecoration: 'none' }}
                    >
                      View in chat →
                    </a>
                  )}
                </div>
                {item.type === 'chat_event_invite' ? (
                  <button
                    type="button"
                    onClick={() => handleAcceptChatEventInvite(item as CalendarItem)}
                    disabled={acceptingInviteId === item.id}
                    style={{
                      padding: '8px 16px',
                      background: acceptingInviteId === item.id ? '#e5e7eb' : 'linear-gradient(135deg, #ff9a56 0%, #ff6b35 100%)',
                      color: acceptingInviteId === item.id ? '#666' : 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: acceptingInviteId === item.id ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {acceptingInviteId === item.id ? 'Adding…' : "I'm in"}
                  </button>
                ) : item.status && (
                  <span style={{
                    padding: '4px 12px',
                    borderRadius: '20px',
                    fontSize: '12px',
                    fontWeight: 600,
                    background: item.status === 'confirmed' || item.status === 'accepted' ? '#d4edda' : item.status === 'cancelled' || item.status === 'declined' ? '#f8d7da' : '#fff3cd',
                    color: item.status === 'confirmed' || item.status === 'accepted' ? '#155724' : item.status === 'cancelled' || item.status === 'declined' ? '#721c24' : '#856404',
                  }}>
                    {item.status}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Calendar;
